import type {
  Context,
  Component,
  Flow,
  GlobalComponentLocation,
  UserData,
} from '../api/models/types'

import { Controller } from '@hotwired/stimulus'
import { handleRequest } from '../api/handleRequest'
import { cloneDeep, isEqual } from 'lodash'
import stylesGenerator from '../api/styles/generator'
import { connectUserData, getFlow, watchFlowUpdates } from '../helpers/controllerHelpers/utils'
import { getWithIds, isBuilderEditingMode, getUrlParam } from '../helpers/utils'
type ExtractedController =
  | (Controller & {
      userDataValueChanged: (forceUpdate?: Boolean) => void
      isRichTextEditor?: boolean
      updateAfterJsonChange?: () => void
      jsonValueChanged?: () => void
    })
  | null

const CONTROLLERS_THAT_NEED_FORCED_UPDATE = {
  condition: 'user_data',
  'conditional-tags': 'json-update',
}

export default class extends Controller {
  declare readonly pathValue: string
  declare readonly typeValue: string
  declare readonly loadingClass: string
  declare urlValue: string | null
  declare originalFlowJson: Flow
  declare currentJson: any
  declare unwatchFlowUpdates: Function
  declare pageIdValue: string
  declare componentIdValue: string
  declare flowIdValue: string
  declare groupIdValue: string
  declare flowEl: HTMLElement | null
  declare fetchIndex: number
  declare type: 'flow' | 'page' | 'component' | 'styles'
  declare selfWithinFlow: Controller | null
  declare watchGlobalComponentsValue: boolean
  currentGlobalComponents?: Array<Component>

  declare selectedComponent: { id: string; key: string; type: string } | null

  static values = {
    path: String,
    flowId: String,
    pageId: String,
    componentId: String,
    groupId: String,
    type: String,
    watchGlobalComponents: Boolean,
  }

  static classes = ['loading']
  disconnectUserData: () => void
  userDataValue: UserData

  connect() {
    const self = this
    this.flowEl = this.element.closest(`[data-controller*="flow"]`)

    if (!this.flowEl) return
    this.disconnectUserData = connectUserData(this)
    this.urlValue = this.flowEl.getAttribute('data-flow-url-value')
    this.originalFlowJson = getFlow(this, true)
    this.currentJson = this.getThisJson(this.originalFlowJson)

    this.unwatchFlowUpdates = watchFlowUpdates(this, (flow: Flow, prevFlow: Flow) =>
      self.flowJsonUpdated(flow, prevFlow)
    )
    if (this.watchGlobalComponentsValue) {
      this.currentGlobalComponents = this.originalFlowJson.components
    }
    if (this.typeValue === 'page') {
      window.addEventListener('savvy:re_render_page', this.onPageReRender)
      window.addEventListener('savvy:re_render_global_components', this.onGlobalComponentsRender)
    }
  }

  disconnect() {
    if (this.unwatchFlowUpdates) this.unwatchFlowUpdates()
    if (this.disconnectUserData) this.disconnectUserData()
    if (this.typeValue === 'page') {
      window.removeEventListener('savvy:re_render_page', this.onPageReRender)
      window.removeEventListener('savvy:re_render_global_components', this.onGlobalComponentsRender)
    }
  }

  onPageReRender = (event) => {
    let { flowElement, flowId, pageId, forceWholePageRenderization = false } = event.detail

    if (
      ((flowElement && this.flowEl?.isEqualNode(flowElement)) || flowId === this.flowIdValue) &&
      (pageId === this.pageIdValue || !pageId)
    ) {
      let mostRecentJson: JSON
      mostRecentJson = this.getThisJson(getFlow(this, true))
      this.updatePage(mostRecentJson, forceWholePageRenderization)
        .then(() => {
          //* I need to trigger a re-render of global components too
          const globalComponentsEvent = new CustomEvent('savvy:re_render_global_components', {
            detail: {
              flowId,
              flowElement,
            },
          })
          window.dispatchEvent(globalComponentsEvent)
        })
        .catch((error) => this.reportError(error))
    }
  }

  onGlobalComponentsRender = (event) => {
    const { flowId } = event.detail
    let thisFlowIdValue = this.flowIdValue || this.flowEl?.getAttribute('data-flow-id-value')
    if (flowId === thisFlowIdValue) {
      const mostRecentJson = getFlow(this, true)
      if (mostRecentJson) this.updateGlobalComponents(mostRecentJson, true)
    }
  }

  getThisJson(flowJson: Flow) {
    if (!flowJson) return flowJson
    try {
      const json = getWithIds(flowJson, this.pathValue.split('.'), true)
      return json
    } catch (error) {
      return null
    }
  }

  flowJsonUpdated(flowJson: Flow, prevFlowJson?: Flow) {
    // Using prevFlowJson here is sort of doubling up on setting this.currentJson at the end of this function,
    // but there are cases where that doesn't yet work e.g. changing pages
    this.currentJson =
      (prevFlowJson && this.getThisJson(prevFlowJson)) ||
      this.currentJson ||
      this.getThisJson(this.originalFlowJson)

    const newJson = this.getThisJson(flowJson) || this.currentJson

    try {
      if (this.watchGlobalComponentsValue) this.updateGlobalComponents(flowJson)
      const withoutTempValues = cleanTempValues(newJson) as any

      const jsonsAreEqual = treatAsEqual(
        this.typeValue,
        this.currentJson,
        withoutTempValues,
        this.currentJson.builder_version
      )
      // const pageComponentWasRemoved = !newJson && this.pathValue.startsWith('pages.')

      if (jsonsAreEqual) return

      switch (this.typeValue) {
        case 'page': {
          this.updatePage(newJson).catch((error) => this.reportError(error))
          break
        }
        case 'component': {
          this.updateComponent(newJson, cloneDeep(this.currentJson)).catch((error) =>
            this.reportError(error)
          )
          break
        }
        case 'styles': {
          this.updateStyles(flowJson)
          break
        }
      }
    } catch (error) {
      this.reportError(error)
    }

    this.currentJson = newJson
  }

  async updatePage(originalNewPageJson: any, forceEntireReRender = false) {
    const self = this
    const newPageJson = cloneDeep(originalNewPageJson)

    if (forceEntireReRender) return reRenderWholePage()
    const currentJson = cloneDeep(this.currentJson)

    if (treatAsEqual('page', newPageJson, currentJson, this.currentJson.builder_version)) {
      return Promise.resolve()
    }

    //* First we update Page Tags
    if (!isEqual(newPageJson?.tags, currentJson?.tags)) {
      // For tags in new json but not in old, add them as classes
      if (!currentJson?.tags) currentJson.tags = []
      if (!newPageJson.tags) newPageJson.tags = []

      const tagsToAdd = newPageJson.tags.filter((tag: string) => !currentJson.tags.includes(tag))
      tagsToAdd.forEach((tag: string) => {
        this.element.classList.add(`PageTag-${tag}`)
      })
      // For tags in old json but not in new, remove them as classes
      const tagsToRemove = currentJson.tags.filter((tag: string) => !newPageJson.tags.includes(tag))
      tagsToRemove.forEach((tag: string) => {
        this.element.classList.remove(`PageTag-${tag}`)
      })
    }

    //!TODO update conditions tags ------------------------------------------------------------------------------------------------

    //TODO add a try catch to force a whole rerender if something fails???

    //* I create an array of the new components
    const newComponents = (newPageJson?.components || []).filter(
      (component: Component) =>
        !(currentJson.components || []).find(
          (newComponent: Component) => newComponent.id === component.id
        )
    )

    //* I create an array of removed components
    const removedComponents = (this.currentJson.components || []).filter(
      (component: Component) =>
        !newPageJson.components.find((c: Component) => c.id === component.id)
    )

    //* I create an array of the elements being updated on position (disregard adding and removing)
    const currentComponentsWithAddAndRemove = cloneDeep(currentJson.components) || []
    newPageJson.components.forEach((component: Component, index: number) => {
      if (!currentComponentsWithAddAndRemove.find((c: Component) => c.id === component.id)) {
        currentComponentsWithAddAndRemove.splice(index, 0, component)
      }
    })

    //* If there are diff without considering adding and removing, I push it into the movingComponents Array
    let movingElements: Array<HTMLElement> = []
    let movingComponents: Array<Component> = []
    let movingComponentsIds: Array<string> = []
    if (
      !treatAsEqual(
        'components',
        currentComponentsWithAddAndRemove,
        newPageJson.components,
        this.currentJson.builder_version
      )
    ) {
      //* I create an array of the components that changed
      movingComponents = newPageJson.components.filter((component: Component) => {
        let parentId = this.getParentId(component, newPageJson.components)
        let siblingId = this.getNextSiblingId(component, newPageJson.components)

        let currentComponent = currentComponentsWithAddAndRemove.find(
          (c: Component) => c.id === component.id
        ) as Component
        let currentParentId = this.getParentId(currentComponent, currentComponentsWithAddAndRemove)
        let currentSiblingId = this.getNextSiblingId(
          currentComponent,
          currentComponentsWithAddAndRemove
        )
        if (parentId !== currentParentId || siblingId !== currentSiblingId) {
          movingComponentsIds.push(component.id)
          return true
        }
        return false
      })

      for (const component of movingComponents) {
        movingElements.push(
          this.element.querySelector(`[data-component-id-value="${component.id}"]`) as HTMLElement
        )
      }
    }

    //* This is the update for adding a new component
    if (newComponents.length > 0) {
      //* I need to add containers before its childrens to avoid failing if a component is added before its parent
      //* I need to recursively order the new components array to always have children after parents
      let componentsToSort = JSON.parse(JSON.stringify(newComponents))
      let sortedNewComponents: Array<Component> = []

      const addChildComponents = (parentId: string) => {
        componentsToSort.forEach((component: Component) => {
          let parent_id = this.getParentId(component)
          if (parent_id === parentId) {
            sortedNewComponents.push(component)
            if (component.type === 'Container') addChildComponents(component.id)
          }
        })
      }

      componentsToSort.forEach((component: Component) => {
        let parentId = this.getParentId(component)
        if (!parentId || !newComponents.find((c: Component) => c.id === parentId)) {
          sortedNewComponents.push(component)

          if (component.type === 'Container') addChildComponents(component.id)
        }
      })

      for (const component of sortedNewComponents) {
        if (component?.__is_child_already_inserted) continue
        //* For each new component I fetch the html
        await this.getComponentElement(component).then((componentHtml) => {
          if (!componentHtml) return
          let temporaryContainer = document.createElement('div')
          temporaryContainer.innerHTML = componentHtml as string

          const newElement = temporaryContainer.firstChild as HTMLElement
          //* I obtain the siblingElement, disregarding any following components that isn't in the DOM yet
          let componentIndexFlag: Boolean = false
          let filteredComponents = newPageJson.components.filter((c: Component) => {
            if (componentIndexFlag) {
              return currentJson.components?.find((oldComp: Component) => c.id === oldComp.id)
            }
            if (c.id === component.id) componentIndexFlag = true
            return true
          })

          let siblingElement: HTMLElement | null = this.getNextSiblingElement(
            component,
            filteredComponents
          )

          if (newElement) {
            let parentId = this.getParentId(component, newPageJson.components)
            const parent = parentId
              ? this.element.querySelector(`[data-component-id-value="${parentId}"]`)
              : null
            if (parent) {
              //* If element has a parent, I insert it before the siblingElement or at the end of the parent
              siblingElement
                ? parent.insertBefore(newElement, siblingElement)
                : parent.appendChild(newElement)
            } else {
              this.insertPageElement(newElement, siblingElement)
            }
          }
        })
      }
    }

    //* This is the update for removing a component
    if (removedComponents.length > 0) {
      //* For each removed component I remove the element from the DOM
      removedComponents.forEach((component: Component) => {
        const elementToRemove = this.element.querySelector(
          `[data-component-id-value="${component.id}"]`
        )
        elementToRemove?.remove()
      })
    }

    //* This is the update for updating a component position (content handled in updateComponent)
    if (movingComponents.length > 0) {
      for (const component of movingComponents) {
        const elementToMove = movingElements[movingComponents.indexOf(component)]

        //* I obtain the siblingElement, disregarding any following components that will be updated
        let componentIndexFlag: Boolean = false
        let filteredComponents = newPageJson.components.filter((c: Component) => {
          if (componentIndexFlag) {
            return !movingComponentsIds.includes(c.id)
          }
          if (c.id === component.id) componentIndexFlag = true
          return true
        })

        const siblingElement = this.getNextSiblingElement(component, filteredComponents)

        if (elementToMove) {
          let parentId = this.getParentId(component, newPageJson.components)
          const parentElement = parentId
            ? this.element.querySelector(`[data-component-id-value="${parentId}"]`)
            : null
          if (parentElement) {
            //* If element has a parent, I insert it before the siblingElement or at the end of the parent
            siblingElement
              ? parentElement.insertBefore(elementToMove, siblingElement)
              : parentElement.appendChild(elementToMove)
          } else {
            this.insertPageElement(elementToMove, siblingElement)
          }
        }
      }
    }

    this.element
      .getAttribute('data-controller')
      ?.split(' ')
      .forEach((identifier) => {
        if (Object.keys(CONTROLLERS_THAT_NEED_FORCED_UPDATE).includes(identifier)) {
          const controller = this.application.getControllerForElementAndIdentifier(
            this.element,
            identifier
          ) as ExtractedController
          if (CONTROLLERS_THAT_NEED_FORCED_UPDATE[identifier] === 'json-update')
            controller?.jsonValueChanged?.()
        }
      })

    async function reRenderWholePage() {
      await self.getPageHtml(newPageJson).then((pageHtml) => {
        if (!pageHtml) return
        const temporaryContainer = document.createElement('div')
        temporaryContainer.innerHTML = pageHtml
        const newPageElement = temporaryContainer as HTMLElement

        if (newPageElement) {
          self.element.innerHTML = newPageElement.innerHTML
        }
      })
    }
  }

  updateComponent(newComponentJson: any, prevComponentJson: any) {
    if (!newComponentJson) {
      return Promise.resolve()
    }

    if (newComponentJson.__is_child_already_inserted) return Promise.resolve()

    // @TODO: Generalise to not just components
    if (
      treatAsEqual(
        'component',
        newComponentJson,
        prevComponentJson,
        this.currentJson.builder_version
      )
    ) {
      return Promise.resolve()
    }

    this.currentJson = newComponentJson

    // const componentChildren: Component[] = []

    // if (newComponentJson.type === 'Container') {
    //   const componentsOnPage = getWithIds(flowJson, this.pathValue.split('.').slice(0, -1), true)

    //   addChildComponents(newComponentJson.key)

    //   function addChildComponents(parentKey) {
    //     componentsOnPage.forEach((component) => {
    //       if (component.parent_key === parentKey) {
    //         componentChildren.push(component)
    //         if (component.type === 'Container') addChildComponents(component.key)
    //       }
    //     })
    //   }
    // }

    return this.getComponentElement(newComponentJson).then((componentHtml) => {
      if (!componentHtml) return

      //* We update the innerHTML of the element to avoid triggering infinite loops (and maybe losing event listeners).
      //* We then update all the relevant attributes on the top-level element.
      const tempParentElement = document.createElement('div')
      tempParentElement.innerHTML = componentHtml
      const tempElement = tempParentElement.firstChild as any as HTMLElement

      //* For components that are not containers, I replace the innerHTML of the element (containers does not have innerHTML)
      if (newComponentJson.type !== 'Container') this.element.innerHTML = tempElement?.innerHTML

      //* I update the attributes of the top-level element
      const attributesToUpdate = Array.from(
        new Set([...this.element.getAttributeNames(), ...tempElement.getAttributeNames()])
      )

      attributesToUpdate.forEach((attribute) => {
        const value = tempElement.getAttribute(attribute)
        if (
          value &&
          this.element.getAttribute(attribute) !== value &&
          attribute !== 'data-json-update-path-value'
        )
          if (!this.element.getAttribute(`${attribute}-suppressed`))
            this.element.setAttribute(attribute, value)
          else this.element.setAttribute(`${attribute}-suppressed`, value)
      })

      this.element
        .getAttribute('data-controller')
        ?.split(' ')
        .forEach((identifier) => {
          if (Object.keys(CONTROLLERS_THAT_NEED_FORCED_UPDATE).includes(identifier)) {
            const controller = this.application.getControllerForElementAndIdentifier(
              this.element,
              identifier
            ) as ExtractedController
            if (CONTROLLERS_THAT_NEED_FORCED_UPDATE[identifier] === 'user_data')
              controller?.userDataValueChanged()

            if (
              newComponentJson.type === 'RichTextMarkdown' &&
              controller?.isRichTextEditor &&
              CONTROLLERS_THAT_NEED_FORCED_UPDATE[identifier] === 'reconnect'
            )
              controller?.updateAfterJsonChange?.()

            if (CONTROLLERS_THAT_NEED_FORCED_UPDATE[identifier] === 'json-update')
              controller?.jsonValueChanged?.()
          }
        })

      //! TODO: I should add sth here to update the hidden property of a component ------------------------------------------------------------------------------------------------
    })
  }

  updateStyles(newFlowJson: any) {
    const cssText = stylesGenerator(newFlowJson, this.flowIdValue, false)
    const styleEl = this.element.parentElement?.querySelector('style')
    if (styleEl) styleEl.innerHTML = cssText

    // document
    //   .querySelectorAll(`style[id="savvy-styles-${this.flowIdValue}"]`)
    //   .forEach((stylesEl) => (stylesEl.innerHTML = cssText))
  }

  async updateGlobalComponents(flowJson: Flow, forceEntireReRender: boolean = false) {
    if (forceEntireReRender) return this.reRenderAllGlobalComponents(flowJson)

    const updatedComponents = flowJson.components || []

    let currentGlobalComponents = this.currentGlobalComponents || []
    if (
      treatAsEqual(
        'components',
        currentGlobalComponents,
        updatedComponents,
        this.currentJson.builder_version
      )
    )
      return

    //* I create an array of the new components and their location
    const newComponents = updatedComponents
      .map((newComponent) =>
        !(this.currentGlobalComponents || []).find((component) => newComponent.id === component.id)
          ? [newComponent, newComponent._location]
          : null
      )
      .filter((item) => item) as Array<[Component, GlobalComponentLocation]>

    //* I create an array of removed components
    const removedComponents = (this.currentGlobalComponents || []).filter(
      (component) => !updatedComponents.find((c) => c.id === component.id)
    )

    //* I create an array of the elements being updated on position (disregard adding and removing)
    const currentGlobalComponentsWithAddAndRemove = [...currentGlobalComponents]
    updatedComponents.forEach((component, index) => {
      if (!currentGlobalComponentsWithAddAndRemove.find((c) => c.id === component.id)) {
        currentGlobalComponentsWithAddAndRemove.splice(index, 0, component)
      }
    })
    //* If there are diff without considering adding and removing, I push it into the movingComponents Array
    let movingElements: Array<HTMLElement> = []
    let movingComponents: Array<Component> = []
    let movingComponentsIds: Array<string> = []
    if (
      !treatAsEqual(
        'components',
        currentGlobalComponentsWithAddAndRemove,
        updatedComponents,
        this.currentJson.builder_version
      )
    ) {
      //* I create an array of the components that changed
      movingComponents = updatedComponents.filter((component) => {
        let parentId = this.getParentId(component)
        let siblingId = this.getNextSiblingId(component, updatedComponents, component._location)

        let currentGlobalComponent = currentGlobalComponentsWithAddAndRemove.find(
          (c) => c.id === component.id
        ) as Component
        let currentParentId = this.getParentId(currentGlobalComponent)
        let currentSiblingId = this.getNextSiblingId(
          currentGlobalComponent,
          currentGlobalComponentsWithAddAndRemove,
          currentGlobalComponent._location
        )
        if (
          parentId !== currentParentId ||
          siblingId !== currentSiblingId ||
          component._location !== currentGlobalComponent._location
        ) {
          movingComponentsIds.push(component.id)
          return true
        }
        return false
      })

      for (const component of movingComponents) {
        movingElements.push(
          this.element.querySelector(`[data-component-id-value="${component.id}"]`) as HTMLElement
        )
      }
    }

    //* This is the update for adding a new component
    if (newComponents.length > 0) {
      //* I need to add first containers to avoid failing if a component is added before its parent
      //* I order the new components array to have first the containers
      newComponents.sort((a, b) => {
        if (a[0].type === 'Container' && b[0].type !== 'Container') return -1
        if (a[0].type !== 'Container' && b[0].type === 'Container') return 1
        return 0
      })

      for (const [component, location] of newComponents) {
        if (component?.__is_child_already_inserted) continue
        //* For each new component I fetch the html
        await this.getComponentElement(component).then((componentHtml) => {
          if (!componentHtml) return
          let temporaryContainer = document.createElement('div')
          temporaryContainer.innerHTML = componentHtml as string
          if (temporaryContainer.firstChild) {
            temporaryContainer.firstElementChild?.setAttribute('data-global-component', 'true')
            temporaryContainer.firstElementChild?.setAttribute(
              'data-global-component-location',
              location
            )
          }

          const newElement = temporaryContainer.firstChild as HTMLElement
          //* I obtain the siblingElement, disregarding any following components that isn't in the DOM yet
          let componentIndexFlag: Boolean = false
          let filteredComponents = updatedComponents.filter((c) => {
            if (componentIndexFlag) {
              return this.currentGlobalComponents?.find((oldComp) => c.id === oldComp.id)
            }
            if (c.id === component.id) componentIndexFlag = true
            return true
          })

          let siblingElement: HTMLElement | null = this.getNextSiblingElement(
            component,
            filteredComponents,
            location
          )

          if (newElement) {
            let parentId = this.getParentId(component, updatedComponents)
            const parent = parentId
              ? this.element.querySelector(`[data-component-id-value="${parentId}"]`)
              : null
            if (parent) {
              //* If element has a parent, I insert it before the siblingElement or at the end of the parent
              siblingElement
                ? parent.insertBefore(newElement, siblingElement)
                : parent.appendChild(newElement)
            } else {
              this.insertGlobalElement(newElement, siblingElement, updatedComponents, location)
            }
          }
        })
      }
    }

    //* This is the update for removing a component
    if (removedComponents.length > 0) {
      //* For each removed component I remove the element from the DOM
      removedComponents.forEach((component) => {
        const elementToRemove = this.element.querySelector(
          `[data-component-id-value="${component.id}"]`
        )
        elementToRemove?.remove()
      })
    }

    //* This is the update for updating a component position (content handled in updateComponent)
    if (movingComponents.length > 0) {
      for (const component of movingComponents) {
        const elementToMove = movingElements[movingComponents.indexOf(component)]

        //* I obtain the siblingElement, disregarding any following components that will be updated
        let componentIndexFlag: Boolean = false
        let filteredComponents = updatedComponents.filter((c) => {
          if (componentIndexFlag) {
            return !movingComponentsIds.includes(c.id)
          }
          if (c.id === component.id) componentIndexFlag = true
          return true
        })

        const siblingElement = this.getNextSiblingElement(
          component,
          filteredComponents,
          component._location
        )

        if (elementToMove) {
          let parentId = this.getParentId(component, updatedComponents)
          const parentElement = parentId
            ? this.element.querySelector(`[data-component-id-value="${parentId}"]`)
            : null
          if (parentElement) {
            //* If element has a parent, I insert it before the siblingElement or at the end of the parent
            siblingElement
              ? parentElement.insertBefore(elementToMove, siblingElement)
              : parentElement.appendChild(elementToMove)
          } else {
            //* In builder v4, the parent_key is deprecated and only the parent_id is used
            const parentRef =
              this.currentJson.builder_version > 3
                ? component.parent_id
                : component.parent_key || component.parent_id
            this.insertGlobalElement(
              elementToMove,
              siblingElement,
              filteredComponents,
              parentRef ? undefined : component._location
            )
          }
        }
      }

      //! @TODO: Rerender all global components
    }

    this.currentGlobalComponents = updatedComponents
  }

  async reRenderAllGlobalComponents(flowJson: Flow) {
    if (!this) return

    let components = flowJson.components || []

    //* I obtain the current page
    let currentPageId = this.userDataValue.current_page_id
    let currentPageJson = flowJson.pages.find((page) => page.id === currentPageId)

    //* I create an array of all the global HTML Elements to render
    let componentsToRender = cloneDeep(components)
    //* If the component has hide true or is hidden by a parent ancestor, I remove it from the array
    componentsToRender = componentsToRender.filter((component) => {
      //* In builder v4, the parent_key is deprecated and only the parent_id is used
      let parent = componentsToRender.find((c) =>
        this.currentJson.builder_version > 3
          ? c.id === component.parent_id
          : c.key === component.parent_key || c.id === component.parent_id
      )
      while (parent) {
        if (parent.hide) return false
        parent = componentsToRender.find((c) =>
          this.currentJson.builder_version > 3
            ? c.id === parent?.parent_id
            : c.key === parent?.parent_key || c.id === parent?.parent_id
        )
      }
      return !component.hide
    })

    let elementsToRender: HTMLElement[] = []
    for (const component of componentsToRender) {
      //* For each component I fetch the html
      await this.getComponentElement(component).then((componentHtml: string | void) => {
        if (!componentHtml) return
        let temporaryContainer = document.createElement('div')
        temporaryContainer.innerHTML = componentHtml as string
        if (temporaryContainer.firstChild) {
          temporaryContainer.firstElementChild?.setAttribute('data-global-component', 'true')
          temporaryContainer.firstElementChild?.setAttribute(
            'data-global-component-location',
            component._location || ''
          )
        }

        const newElement = temporaryContainer.firstChild as HTMLElement

        //* I need to add the Page Tags to the new element
        let currentPageTags = currentPageJson?.tags || []
        currentPageTags.forEach((tag: string) => {
          newElement.classList?.add(`PageTag-${tag}`)
        })

        if (newElement) elementsToRender.push(newElement)
      })
    }

    //* First I need to remove current global positions from the dom
    let flowEl = this.element.closest(`[data-controller*="flow"]`) as HTMLElement
    let globalElements = flowEl.querySelectorAll('[data-global-component="true"]')
    globalElements.forEach((element) => {
      element.remove()
    })

    //* I loop through the elementsToRender and if they have a parent I append them to the parent and remove it from the array
    let elementsToRenderCopy = [...elementsToRender]
    let elementsToRenderCopyOriginalLength = elementsToRenderCopy.length
    for (let i = 0; i < elementsToRenderCopyOriginalLength; i++) {
      let element = elementsToRenderCopy[i]
      let component = componentsToRender[i]
      let parentId = this.getParentId(component, components)
      if (parentId) {
        let parent: HTMLElement | null = null
        for (const el of elementsToRender) {
          if (el.getAttribute('data-component-id-value') === parentId) parent = el as HTMLElement
          let posiblyChildrenWithID = el.querySelector(`[data-component-id-value="${parentId}"]`)
          if (posiblyChildrenWithID) parent = posiblyChildrenWithID as HTMLElement
        }
        if (parent && !element.contains(parent)) {
          parent.appendChild(element)
          elementsToRender = elementsToRender.filter(
            (el) => el.getAttribute('data-component-id-value') !== component.id
          )
        }
      }
    }

    //* I insert every element in the DOM (the remaining ones that don't have a parent)
    for (const newElement of elementsToRender) {
      let component = componentsToRender.find(
        (comp) => comp.id === newElement.getAttribute('data-component-id-value')
      ) as Component
      let siblingElement: HTMLElement | null = this.getNextSiblingElement(
        component,
        [],
        component._location
      )

      //* I need to pass the componentsToRender filtered until this component included
      const componentIndex = componentsToRender.indexOf(component)
      const componentsToRenderUntilThisComponent = componentsToRender.slice(0, componentIndex + 1)
      this.insertGlobalElement(
        newElement,
        siblingElement,
        componentsToRenderUntilThisComponent,
        component._location
      )
    }
  }

  async getComponentElement(componentJson: any): Promise<string | void> {
    if (!componentJson || Object.keys(componentJson).length === 0) return

    // @TODO: Generalise to not just components

    const self = this
    const url = this.getfetchUrl(
      `page/${this.pageIdValue || 'global'}/component/${componentJson?.id}`
    )

    this.fetchIndex = (this.fetchIndex || 0) + 1
    const fetchIndex = this.fetchIndex

    this.setLoading(true)

    const context: Context = {
      env: 'local',
      fetch: (...args: any[]) => fetch(args[0], args[1]),
      flowsBucket: {},
    }
    const requestURL = new URL(url)
    const headers = new Headers({
      randomId: Math.random().toString().slice(2),
    })
    const flow = getFlow(this, true)
    const body = {
      json: {
        flowId: this.originalFlowJson.id,
        groupId: this.watchGlobalComponentsValue
          ? this.element.getAttribute('data-flow-group-id-value')
          : this.groupIdValue,
        flow,
        component: componentJson,
        page: {
          ...getFlow(this, true)?.pages.find((p) => p.id === this.userDataValue.current_page_id),
          id: this.userDataValue.current_page_id,
          key: this.userDataValue.current_page_key,
        },
      },
    }

    const request = {
      url: requestURL,
      method: 'POST',
      headers,
      body,
    }

    return handleRequest(context, request).then(({ responseData: componentHtml }) => {
      if (fetchIndex !== self.fetchIndex) return

      self.setLoading(false)
      return componentJson ? componentHtml : ''
    })
  }

  async getPageHtml(pageJson: any): Promise<string | void> {
    const self = this

    const flow = getFlow(this, true)

    if (!flow.pages.find((p) => p.id === pageJson.id)) return

    const url = this.getfetchUrl(`page/${this.pageIdValue || 'global'}/`)

    this.fetchIndex = (this.fetchIndex || 0) + 1
    const fetchIndex = this.fetchIndex

    this.setLoading(true)

    // @TODO: Find a way to make this return the outer HTML of the page, not the inner HTML

    const context: Context = {
      env: 'local',
      fetch: (...args: any[]) => fetch(args[0], args[1]),
      flowsBucket: {},
    }
    const requestURL = new URL(url)
    const headers = new Headers({
      randomId: Math.random().toString().slice(2),
    })

    const body = {
      json: {
        flowId: this.originalFlowJson.id,
        groupId: this.groupIdValue,
        flow,
        page: pageJson,
      },
    }

    const request = {
      url: requestURL,
      method: 'POST',
      headers,
      body,
    }

    return handleRequest(context, request).then(({ responseData: pageHtml }) => {
      if (fetchIndex !== self.fetchIndex) return

      self.setLoading(false)
      return pageJson ? pageHtml : ''
    })
  }

  getfetchUrl(subpath) {
    const urlObject = new URL(`${this.urlValue}/${subpath}`)
    urlObject.searchParams.set(
      'version',
      this.flowEl?.getAttribute('data-flow-version-value') || '0'
    )
    const branchId = this.flowEl?.getAttribute('data-flow-branch-id-value')
    if (branchId && branchId !== 'null') urlObject.searchParams.set('embeddable_branch', branchId)

    //* If using url param embeddable_draft, I need to add it to the url
    const embeddableDraftId = getUrlParam('embeddable_draft')
    if (embeddableDraftId) urlObject.searchParams.set('embeddable_draft', embeddableDraftId)

    //* if using url param live, I need to add it to the url
    const live = getUrlParam('live')
    if (live) urlObject.searchParams.set('live', live)

    // if current url isBuilderEditingMode, add savvy_editing=true to the new url
    if (isBuilderEditingMode()) {
      urlObject.searchParams.set('savvy_editing', 'true')
    }
    return urlObject.toString()
  }

  setLoading(loading) {
    this.element.classList.toggle(this.loadingClass, loading)
  }

  reportError(error) {
    console.error(`Error rendering updates on the page (unrelated to JSON): ${error}`)
  }

  getNextSiblingId(
    component: Component,
    components: Component[],
    location?: GlobalComponentLocation
  ) {
    let newComponentIndex = components.findIndex((c) => c.id === component.id)
    let siblingId: string | null = null
    let parentId: string | null = this.getParentId(component, components)

    let i = newComponentIndex + 1
    while (i < components.length && !siblingId) {
      //* If new component has a parent, I look for siblings with the same parent
      if (parentId) {
        let comparingComponentParentId: string | null = this.getParentId(components[i], components)
        if (comparingComponentParentId === parentId) {
          siblingId = components[i].id
        }
      }

      //* If new component has no parent, I look for siblings with no parent (if a location is provded I search in the same location)
      const componentParentRef =
        this.currentJson.builder_version > 3
          ? component.parent_id
          : component.parent_key || component.parent_id

      const comparingComponentParentRef =
        this.currentJson.builder_version > 3
          ? components[i].parent_id
          : components[i].parent_key || components[i].parent_id

      if (!componentParentRef) {
        if (location && !comparingComponentParentRef && components[i]._location === location) {
          siblingId = components[i].id
        }
        if (!location && !comparingComponentParentRef) {
          siblingId = components[i].id
        }
      }

      i++
    }
    return siblingId
  }

  getNextSiblingElement(
    component: Component,
    components: Component[],
    location?: GlobalComponentLocation
  ) {
    let siblingElement: HTMLElement | null = null
    let siblingId = this.getNextSiblingId(component, components, location)

    if (siblingId) {
      siblingElement = this.element.querySelector<HTMLElement>(
        `[data-component-id-value="${siblingId}"]`
      )
    }
    return siblingElement
  }

  getComponentIdFromKey(key: string | undefined, components?: Component[]) {
    if (!key) return null
    components = components || getFlow(this, true).components
    return components?.find((c) => c.key === key)?.id || null
  }

  getParentId(component: Component, components?: Component[]) {
    //* In builder v4, the parent_key is deprecated and only the parent_id is used
    return this.currentJson.builder_version > 3
      ? component.parent_id || null
      : component.parent_id || this.getComponentIdFromKey(component.parent_key, components)
  }

  insertPageElement(element: HTMLElement, siblingElement: HTMLElement | null) {
    //* If element has no parent, I need to insert it in the pageContents
    const pageContent = this.element.querySelector('.ElementType-PageContents') as HTMLElement

    if (siblingElement) {
      pageContent.insertBefore(element, siblingElement)
    } else {
      //* If no siblingElement, I insert it at the end of the pageContent (before the global components)
      //* I look for the first global component ([data-global-component=true]) with data-global-component-location = after_components
      let firstGlobalComponentAfterComponents = pageContent.querySelector(
        '[data-global-component-location="after_components"]'
      )
      if (firstGlobalComponentAfterComponents) {
        pageContent.insertBefore(element, firstGlobalComponentAfterComponents)
      } else {
        pageContent.appendChild(element)
      }
    }
  }

  insertGlobalElement(
    element: HTMLElement,
    siblingElement: HTMLElement | null,
    components: Component[],
    location?: GlobalComponentLocation
  ) {
    //* If element has no parent, I need to respond depending on the location
    const page = this.flowEl?.querySelector('[data-controller*="page"]')
    const pageContent = this.flowEl?.querySelector('.ElementType-PageContents')

    let referenceElement: HTMLElement | null = null

    //* I obtain the number of global components with the same location

    let numberOfGlobalComponentsInSameLocation =
      components.filter((component) => {
        //* In builder v4, the parent_key is deprecated and only the parent_id is used
        const parentRef =
          this.currentJson.builder_version > 3
            ? component.parent_id
            : component.parent_key || component.parent_id
        return !parentRef && component._location === location
      }).length - 1

    switch (location) {
      case 'before_page':
        //* The referenceElement is the siblingElement or the page
        referenceElement = siblingElement ? siblingElement : (page as HTMLElement)
        this.flowEl?.insertBefore(element, referenceElement)
        break

      case 'after_page':
        //* The referenceElement is the siblingElement or just null to append it at the end of the flow
        referenceElement = siblingElement ? siblingElement : null
        referenceElement
          ? this.flowEl?.insertBefore(element, referenceElement)
          : this.flowEl?.appendChild(element)
        break

      case 'page_top':
        //* The referenceElement is the siblingElement or the pageContent
        referenceElement = siblingElement ? siblingElement : (pageContent as HTMLElement)
        page && page.insertBefore(element, referenceElement)
        break

      case 'page_bottom':
        //* The referenceElement is the siblingElement or just null to append it at the end of the page
        referenceElement = siblingElement ? siblingElement : null
        page && referenceElement
          ? page.insertBefore(element, referenceElement)
          : page?.appendChild(element)
        break

      case 'before_components':
        //* The referenceElement is the siblingElement or the first component in the page (n.child after number of global components before components)
        referenceElement = siblingElement
          ? siblingElement
          : (pageContent?.children[numberOfGlobalComponentsInSameLocation] as HTMLElement)
        pageContent && pageContent.insertBefore(element, referenceElement)
        break

      case 'after_components':
        //* The referenceElement is the siblingElement or just null to append it at the end of the pageContent
        referenceElement = siblingElement ? siblingElement : null

        pageContent &&
          (referenceElement
            ? pageContent.insertBefore(element, referenceElement)
            : pageContent.appendChild(element))
        break
    }
  }

  removeThisController() {
    const controllers = this.element.getAttribute('data-controller')?.split(' ') || []
    const newControllers = controllers.filter((controller) => controller !== 'json-update')
    this.element.setAttribute('data-controller', newControllers.join(' '))
  }
}

function cleanTempValues(
  json: Record<string, unknown> = {},
  tempKeys = ['isContainerWithChildren', '__is_child_already_inserted']
) {
  const clone = cloneDeep(json)
  Object.keys(clone).forEach((key) => {
    if (typeof clone[key] === 'object' && clone[key] !== null)
      cleanTempValues(clone[key] as Record<string, unknown>)
    else if (tempKeys.includes(key) || (clone.type === 'Container' && key === 'components')) {
      delete clone[key]
    }
  })

  return clone
}

interface Object {
  components: {
    parent_key: any
    parent_id: any
    id: string
    hide?: boolean
  }[]
  [key: string]: unknown
}

function treatAsEqual(
  type: string,
  prevVal: Object | Array<Component> | Component,
  newVal: Object | Array<Component> | Component,
  builderVersion: number = 3
) {
  switch (type) {
    case 'page':
      const page1 = {
        ...prevVal,
        components: (prevVal as Object).components?.map((c) => ({
          id: c.id,
          ...(builderVersion <= 3 && { parent_key: c.parent_key }),
          parent_id: c.parent_id,
        })),
      }
      const page2 = {
        ...newVal,
        components: (newVal as Object).components?.map((c) => ({
          id: c.id,
          ...(builderVersion <= 3 && { parent_key: c.parent_key }),
          parent_id: c.parent_id,
        })),
      }
      return isEqual(page1, page2)

    case 'components':
      const components1 = (prevVal as Array<any>).map((c) => ({
        id: c.id,
        ...(builderVersion <= 3 && { parent_key: c.parent_key }),
        parent_id: c.parent_id,
        _location: c._location,
      }))
      const components2 = (newVal as Array<any>).map((c) => ({
        id: c.id,
        ...(builderVersion <= 3 && { parent_key: c.parent_key }),
        parent_id: c.parent_id,
        _location: c._location,
      }))
      return isEqual(components1, components2)

    case 'component':
      const component1 = {
        ...(prevVal as Component),
        ...(builderVersion <= 3 && { parent_key: undefined }),
        parent_id: undefined,
        _location: undefined,
      }
      const component2 = {
        ...(newVal as Component),
        ...(builderVersion <= 3 && { parent_key: undefined }),
        parent_id: undefined,
        _location: undefined,
      }
      return isEqual(component1, component2)

    default:
      return isEqual(prevVal, newVal)
  }
}
